import React, { useMemo } from 'react'
import styled, { css } from 'styled-components'
import { ReactComponent as FileIcon } from 'assets/images/file-icon.svg'
import { isNotNilOrEmpty } from 'utils/ramda'
import { toCamelCase } from 'js-convert-case'
import { redirect } from 'utils/paths'
import { pathOr, propOr } from 'ramda'
import { Tooltip } from '@mui/material'
import { useDispatch } from 'react-redux'
import { clearBpFilesRoutine } from 'ducks/files/actions'
import { clearSelectedBpRoutine } from 'features/bpDetails/ducks/actions'
import { surface, radius, shadow, font, bp as bpToken, cardStyle } from 'features/dashboard/tokens'

const BpBox = ({ bp, buildingTypes, noAction }) => {
  const { name, uprn, isUnit } = bp
  const dispatch = useDispatch()

  const handleBpClick = () => {
    // Clear Redux state before navigation
    dispatch(clearBpFilesRoutine())
    dispatch(clearSelectedBpRoutine())
    redirect(`bp-list/${bp.id}`)
  }

  const buildingTypesString = useMemo(() => {
    let result = ''
    if (isNotNilOrEmpty(bp.buildingTypes)) {
      bp.buildingTypes.forEach(e => {
        const type = toCamelCase(e.type)
        result += `${pathOr('', [type, 'label'], buildingTypes)};`
      })
    }
    return result.substring(0, result.length - 1)
  }, [bp, buildingTypes])

  return (
    <BpBoxWrapper
      onClick={
      noAction
        ? () => {}
        : handleBpClick
    }
      $noAction={noAction}
    >
      {propOr('', 'status', bp) === 'draft' && !isUnit && (
        <Tooltip title='This Building Passport is still in draft...'>
          <DraftLabel>Draft</DraftLabel>
        </Tooltip>
      )}
      {isUnit && <UnitLabel>Unit Passport</UnitLabel>}

      <DetailsWrapper>
        <IconWrapper>
          <FileIcon />
        </IconWrapper>
        <HeaderTextWrapper>
          <BpName title={name}>{name}</BpName>
          <Uprn>
            UPRN: {isNotNilOrEmpty(uprn) ? uprn : 'Not yet allocated'}
          </Uprn>
        </HeaderTextWrapper>
      </DetailsWrapper>

      <InfoSection>
        <DetailsItem>
          <DetailsLabel>Type</DetailsLabel>
          <DetailsValue>{buildingTypesString}</DetailsValue>
        </DetailsItem>
        <DetailsItem>
          <DetailsLabel>Address</DetailsLabel>
          <DetailsValue title={`${bp.street}, ${bp.city}, ${bp.postalCode}`}>
            {`${bp.street}, ${bp.city}, ${bp.postalCode}`}
          </DetailsValue>
        </DetailsItem>
      </InfoSection>
    </BpBoxWrapper>
  )
}

export default BpBox

const BpBoxWrapper = styled.div`
  position: relative;
  box-sizing: border-box;
  ${cardStyle}
  padding: 20px;
  width: 100%;
  margin-bottom: 16px;
  cursor: pointer;

  @media (min-width: 769px) {
    max-width: 300px;
    height: 160px;
    margin-right: 20px;
    margin-bottom: 20px;
  }

  &:hover {
    background-color: ${({ theme }) => theme.colors.grey[100]};
    border-color: ${({ theme }) => theme.colors.primary}40;
  }

  ${({ $noAction }) => $noAction && css`
    cursor: default;
    &:hover {
      background-color: ${({ theme }) => theme.colors.white};
    }
  `}
`

const DetailsWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 12px;
`

const IconWrapper = styled.div`
  background-color: ${surface.linen};
  border-radius: ${radius.sm};
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  margin-right: 12px;
  width: 55px;
  height: 55px;

  @media (max-width: ${bpToken.mobile}) {
    width: 42px;
    height: 42px;

    svg {
      width: 20px;
      height: 20px;
    }
  }
`

const HeaderTextWrapper = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
`

const BpName = styled.div`
  font-size: ${font.emphasis};
  font-weight: 600;
  line-height: 1.2;
  color: ${({ theme }) => theme.colors.haiti};
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`

const Uprn = styled.div`
  font-size: ${font.small};
  color: ${({ theme }) => theme.colors.grey[600]};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const DetailsItem = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`

const DetailsLabel = styled.div`
  flex-shrink: 0;
  width: 55px;
  font-size: ${font.body};
  color: ${({ theme }) => theme.colors.grey[900]};
  margin-right: 12px;
  padding-left: 4px;

  @media (max-width: ${bpToken.mobile}) {
    width: 42px;
    font-size: ${font.small};
  }
`

const DetailsValue = styled.div`
  flex: 1;
  min-width: 0;
  font-size: ${font.body};
  color: ${({ theme }) => theme.colors.haiti};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const LabelBase = styled.div`
  padding: 4px 8px;
  border-radius: ${radius.xs};
  color: ${({ theme }) => theme.colors.white};
  position: absolute;
  top: 10px;
  right: 10px;
  font-size: ${font.caption};
  font-weight: 600;
  opacity: 0.9;
  z-index: 2;
  box-shadow: ${shadow.card};
`

const DraftLabel = styled(LabelBase)`
  background-color: ${({ theme }) => theme.colors.secondary};
`

const UnitLabel = styled(LabelBase)`
  background-color: ${({ theme }) => theme.colors.primary};
`
